import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Moon, Sun, Calendar, Clock, ChevronRight, Save, Sparkles } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  evening: Moon,
  morning: Sun,
  weekly: Calendar,
  monthly: Calendar,
  situational: Clock
};

const typeLabels = {
  evening: 'Kväll',
  morning: 'Morgon',
  weekly: 'Vecka',
  monthly: 'Månad',
  situational: 'Situation'
};

const typeColors = {
  evening: 'bg-indigo-100/50',
  morning: 'bg-amber-100/50',
  weekly: 'bg-emerald-100/50',
  monthly: 'bg-violet-100/50',
  situational: 'bg-rose-100/50'
};

export default function ReflectionsPage() {
  const { getAuthHeader, hasAccess } = useAuth();
  const navigate = useNavigate();
  
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

  useEffect(() => {
    if (hasAccess) {
      fetchReflections();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Guidade reflektioner</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Guidade reflektioner" />
      </div>
    );
  }

  const fetchReflections = async () => {
    try {
      const response = await axios.get(`${API}/reflections`);
      setReflections(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta reflektioner');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReflection = (reflection) => {
    setSelectedReflection(reflection);
    setCurrentPromptIndex(0);
    setAnswers({});
  };

  const handleAnswerChange = (promptIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [promptIndex]: value
    }));
  };

  const handleNext = () => {
    if (currentPromptIndex < selectedReflection.prompts.length - 1) {
      setCurrentPromptIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPromptIndex > 0) {
      setCurrentPromptIndex(prev => prev - 1);
    }
  };

  const handleSaveToJournal = async () => {
    setSaving(true);
    try {
      // Compile answers into journal entry
      const content = selectedReflection.prompts.map((prompt, index) => {
        return `**${prompt}**\n${answers[index] || '(Inget svar)'}\n`;
      }).join('\n');

      const journalContent = `## ${selectedReflection.title}\n\n${content}\n---\n*${selectedReflection.closing}*`;

      await axios.post(`${API}/journal`, {
        content: journalContent,
        tags: ['reflektion', selectedReflection.type]
      }, {
        headers: getAuthHeader()
      });

      toast.success('Sparad i din dagbok');
      setSelectedReflection(null);
    } catch (error) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const groupedReflections = reflections.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="reflections-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedReflection ? setSelectedReflection(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedReflection ? 'Alla reflektioner' : 'Tillbaka'}
          </Button>
          <h1 className="font-heading text-xl font-light">Guidade reflektioner</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : selectedReflection ? (
          /* Active reflection */
          <div className="animate-fade-in">
            {/* Header */}
            <div className={`${typeColors[selectedReflection.type]} rounded-2xl p-8 mb-8`}>
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const Icon = typeIcons[selectedReflection.type] || Sparkles;
                  return <Icon className="w-6 h-6 text-primary" />;
                })()}
                <span className="text-sm px-3 py-1 rounded-full bg-card/80">
                  {typeLabels[selectedReflection.type]}
                </span>
              </div>
              <h2 className="font-heading text-3xl font-light mb-2">{selectedReflection.title}</h2>
              <p className="text-muted-foreground">{selectedReflection.description}</p>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              {selectedReflection.prompts.map((_, index) => (
                <div 
                  key={index}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index <= currentPromptIndex ? 'bg-primary' : 'bg-secondary'
                  }`}
                />
              ))}
            </div>

            {/* Current prompt */}
            <div className="bg-card rounded-2xl p-8 mb-6">
              <p className="text-sm text-muted-foreground mb-2">
                Fråga {currentPromptIndex + 1} av {selectedReflection.prompts.length}
              </p>
              <h3 className="font-heading text-xl mb-6">
                {selectedReflection.prompts[currentPromptIndex]}
              </h3>
              <Textarea
                value={answers[currentPromptIndex] || ''}
                onChange={(e) => handleAnswerChange(currentPromptIndex, e.target.value)}
                placeholder="Skriv din reflektion här..."
                className="min-h-[150px] rounded-xl bg-secondary/30 border-0 focus-visible:ring-1 focus-visible:ring-primary resize-none"
                data-testid="reflection-textarea"
              />
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentPromptIndex === 0}
                className="rounded-full"
              >
                Föregående
              </Button>
              
              {currentPromptIndex === selectedReflection.prompts.length - 1 ? (
                <Button
                  onClick={handleSaveToJournal}
                  disabled={saving}
                  className="rounded-full bg-primary"
                  data-testid="save-reflection-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Spara i dagboken
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="rounded-full"
                >
                  Nästa
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>

            {/* Closing message (shown at end) */}
            {currentPromptIndex === selectedReflection.prompts.length - 1 && (
              <div className="mt-8 p-6 bg-primary/5 rounded-xl text-center">
                <p className="text-muted-foreground italic">
                  "{selectedReflection.closing}"
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Reflection list */
          <>
            <div className="text-center mb-10 space-y-3 animate-fade-in">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-primary" />
                </div>
              </div>
              <h2 className="font-heading text-3xl font-light">Reflektera & väx</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Guidade frågor för att utforska dina tankar och känslor på ett mjukt sätt.
              </p>
            </div>

            {/* Grouped reflections */}
            <div className="space-y-8">
              {Object.entries(groupedReflections).map(([type, items]) => (
                <div key={type}>
                  <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                    {(() => {
                      const Icon = typeIcons[type] || Sparkles;
                      return <Icon className="w-5 h-5 text-primary" />;
                    })()}
                    {typeLabels[type] === 'Situation' ? 'Situationsbaserade' : `${typeLabels[type]}sreflektioner`}
                  </h3>
                  <div className="grid gap-4">
                    {items.map((reflection, index) => (
                      <button
                        key={reflection.id}
                        onClick={() => handleSelectReflection(reflection)}
                        className={`${typeColors[type]} p-6 rounded-2xl text-left transition-shadow hover:shadow-soft animate-fade-in group`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`reflection-card-${reflection.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-heading text-xl mb-1">{reflection.title}</h4>
                            <p className="text-sm text-muted-foreground">{reflection.description}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {reflection.prompts.length} frågor
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
